import React from 'react';
import { Clock, Sparkles, BarChart3, Settings, Plus } from 'lucide-react';
import { TabType } from '../lib/types';
import { triggerHaptic } from '../lib/haptics';

export type { TabType };

interface BottomNavProps {
  activeTab: TabType;
  onChangeTab: (tab: TabType) => void;
  onOpenQuickLog: () => void;
  isHidden?: boolean;
}

export const BottomNav: React.FC<BottomNavProps> = ({
  activeTab,
  onChangeTab,
  onOpenQuickLog,
  isHidden,
}) => {
  if (isHidden) return null;

  const handleTabPress = (tab: TabType) => {
    triggerHaptic('medium');
    onChangeTab(tab);
  };

  const handleQuickAddPress = () => {
    triggerHaptic('heavy');
    onOpenQuickLog();
  };

  return (
    <nav className="fixed bottom-3 left-4 right-4 z-40 max-w-md mx-auto bg-white/75 dark:bg-[#14161F]/90 backdrop-blur-2xl backdrop-saturate-[200%] border border-slate-200/80 dark:border-zinc-800/80 rounded-[32px] px-3 h-16 flex items-center justify-between shadow-[inset_0_1.5px_1px_rgba(255,255,255,0.8),_0_12px_36px_rgba(0,0,0,0.18)] dark:shadow-[0_12px_36px_rgba(0,0,0,0.7)] transition-all">
      {/* 1. Timeline Tab */}
      <button
        type="button"
        onClick={() => handleTabPress('timeline')}
        className={`flex flex-col items-center justify-center flex-1 transition-all duration-150 active:scale-95 ${
          activeTab === 'timeline'
            ? 'text-slate-950 dark:text-white font-black scale-105'
            : 'text-slate-400 dark:text-zinc-500 hover:text-slate-800 dark:hover:text-zinc-300'
        }`}
      >
        <Clock className="w-5 h-5 mb-0.5" />
        <span className="text-[10px] tracking-tight">Timeline</span>
      </button>

      {/* 2. Actions Tab */}
      <button
        type="button"
        onClick={() => handleTabPress('actions')}
        className={`flex flex-col items-center justify-center flex-1 transition-all duration-150 active:scale-95 ${
          activeTab === 'actions'
            ? 'text-slate-950 dark:text-white font-black scale-105'
            : 'text-slate-400 dark:text-zinc-500 hover:text-slate-800 dark:hover:text-zinc-300'
        }`}
      >
        <Sparkles className="w-5 h-5 mb-0.5" />
        <span className="text-[10px] tracking-tight">Actions</span>
      </button>

      {/* 3. Center Elevated Action Button: Quick Log */}
      <div className="flex-1 flex justify-center -mt-6">
        <button
          type="button"
          onClick={handleQuickAddPress}
          className="w-15 h-15 sm:w-16 sm:h-16 p-3 rounded-full bg-slate-950 dark:bg-white text-white dark:text-black flex items-center justify-center shadow-xl shadow-black/25 active:scale-90 transition-all border-4 border-[#F8F9FA] dark:border-[#090a0f]"
          aria-label="Quick Log"
        >
          <Plus className="w-6 h-6 sm:w-7 sm:h-7 stroke-[3]" />
        </button>
      </div>

      {/* 4. Insights Tab */}
      <button
        type="button"
        onClick={() => handleTabPress('insights')}
        className={`flex flex-col items-center justify-center flex-1 transition-all duration-150 active:scale-95 ${
          activeTab === 'insights'
            ? 'text-slate-950 dark:text-white font-black scale-105'
            : 'text-slate-400 dark:text-zinc-500 hover:text-slate-800 dark:hover:text-zinc-300'
        }`}
      >
        <BarChart3 className="w-5 h-5 mb-0.5" />
        <span className="text-[10px] tracking-tight">Insights</span>
      </button>

      {/* 5. Settings Tab */}
      <button
        type="button"
        onClick={() => handleTabPress('settings')}
        className={`flex flex-col items-center justify-center flex-1 transition-all duration-150 active:scale-95 ${
          activeTab === 'settings'
            ? 'text-slate-950 dark:text-white font-black scale-105'
            : 'text-slate-400 dark:text-zinc-500 hover:text-slate-800 dark:hover:text-zinc-300'
        }`}
      >
        <Settings className="w-5 h-5 mb-0.5" />
        <span className="text-[10px] tracking-tight">Settings</span>
      </button>
    </nav>
  );
};
